import { useEffect, useMemo, useRef, useState } from 'react';

import { Conversation, ConversationParticipantRef } from '../types/Conversation';

interface ConversationFileItem {
  id: string;
  url: string;
  name: string;
  typeKey: string;
  typeLabel: string;
  timestamp: string;
  senderName: string;
}

interface GroupedConversationFiles {
  key: string;
  title: string;
  items: ConversationFileItem[];
}

interface UseConversationFilesOptions {
  selectedConv: Conversation | null;
  getParticipantDisplayName: (participant: ConversationParticipantRef) => string;
  getMessageTypeLabel: (messageType?: string) => string;
}

interface UseConversationFilesResult {
  showFilesPanel: boolean;
  setShowFilesPanel: React.Dispatch<React.SetStateAction<boolean>>;
  filesSearchTerm: string;
  setFilesSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  filesPanelRef: React.RefObject<HTMLDivElement | null>;
  conversationFiles: ConversationFileItem[];
  groupedConversationFiles: GroupedConversationFiles[];
}

export const useConversationFiles = ({
  selectedConv,
  getParticipantDisplayName,
  getMessageTypeLabel,
}: UseConversationFilesOptions): UseConversationFilesResult => {
  const [showFilesPanel, setShowFilesPanel] = useState(false);
  const [filesSearchTerm, setFilesSearchTerm] = useState('');
  const filesPanelRef = useRef<HTMLDivElement | null>(null);

  const conversationFiles = useMemo(() => {
    if (!selectedConv) {
      return [] as ConversationFileItem[];
    }

    return selectedConv.messages
      .filter(message => Boolean(message.mediaUrl) && ['image', 'audio', 'video', 'document'].includes((message.messageType || '').toLowerCase()))
      .map(message => {
        const normalizedType = (message.messageType || '').toLowerCase();
        const typeLabel = getMessageTypeLabel(message.messageType);
        const fallbackName = `${typeLabel} ${new Date(message.timestamp).toLocaleString()}`;
        return {
          id: message.id,
          url: message.mediaUrl as string,
          name: (message.fileName || '').trim() || fallbackName,
          typeKey: normalizedType,
          typeLabel,
          timestamp: message.timestamp,
          senderName: getParticipantDisplayName(message.sender),
        };
      })
      .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());
  }, [getMessageTypeLabel, getParticipantDisplayName, selectedConv]);

  const filteredConversationFiles = useMemo(() => {
    const query = filesSearchTerm.trim().toLowerCase();
    if (!query) {
      return conversationFiles;
    }

    return conversationFiles.filter(file => {
      const text = `${file.name} ${file.typeLabel} ${file.senderName}`.toLowerCase();
      return text.includes(query);
    });
  }, [conversationFiles, filesSearchTerm]);

  const groupedConversationFiles = useMemo(() => {
    const groups: GroupedConversationFiles[] = [
      { key: 'document', title: 'Documents', items: [] },
      { key: 'image', title: 'Images', items: [] },
      { key: 'video', title: 'Videos', items: [] },
      { key: 'audio', title: 'Audio', items: [] },
      { key: 'other', title: 'Other', items: [] },
    ];

    filteredConversationFiles.forEach(file => {
      const group = groups.find(entry => entry.key === file.typeKey) || groups.find(entry => entry.key === 'other');
      if (group) {
        group.items.push(file);
      }
    });

    return groups.filter(group => group.items.length > 0);
  }, [filteredConversationFiles]);

  useEffect(() => {
    setShowFilesPanel(false);
    setFilesSearchTerm('');
  }, [selectedConv?.id]);

  useEffect(() => {
    if (!showFilesPanel) return undefined;

    const onPointerDown = (event: MouseEvent) => {
      if (filesPanelRef.current && !filesPanelRef.current.contains(event.target as Node)) {
        setShowFilesPanel(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [showFilesPanel]);

  return {
    showFilesPanel,
    setShowFilesPanel,
    filesSearchTerm,
    setFilesSearchTerm,
    filesPanelRef,
    conversationFiles,
    groupedConversationFiles,
  };
};